import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { usePlanificacion } from '@context/PlanificacionContext';
import { 
  Home, 
  BookOpen, 
  Calendar, 
  FileText, 
  LogOut, 
  User,
  UserCircle 
} from 'lucide-react';

const Header = () => {
  const { user, isGuest, logout } = useAuth();
  const { area, grado, resetPlanificacion } = usePlanificacion();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    resetPlanificacion();
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-white shadow-sm border-b border-red-100">
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="flex items-center justify-between">

          {/* Logo y título */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-red-400 p-2 rounded-lg group-hover:bg-red-500 transition-colors">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                TuPlan Sociales
              </h1>
              {area && grado ? (
                <p className="text-sm text-gray-500">
                  {area.nombre} — {grado.nombre}
                </p>
              ) : (
                <p className="text-xs text-gray-400 tracking-wide uppercase">
                  Gestión Educativa Perú
                </p>
              )}
            </div>
          </Link>

          {/* Navegación escritorio */}
          {(user || isGuest) && (
            <nav className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/') 
                    ? 'bg-red-50 text-red-600' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Home className="w-4 h-4" />
                Inicio
              </Link>

              {area && grado && (
                <>
                  <Link
                    to="/planificacion"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/planificacion') 
                        ? 'bg-red-50 text-red-600' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    Planificación
                  </Link>

                  <Link
                    to="/generar-documentos"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/generar-documentos') 
                        ? 'bg-red-50 text-red-600' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Documentos
                  </Link>
                </>
              )}
            </nav>
          )}

          {/* Usuario / sesión */}
          <div className="flex items-center gap-3">
            {isGuest && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm">
                <UserCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Modo Invitado</span>
              </div>
            )}

            {user && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{user.nombre}</span>
              </div>
            )}

            {(user || isGuest) && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            )}
          </div>
        </div>

        {/* Navegación móvil */}
        {(user || isGuest) && area && grado && (
          <nav className="md:hidden flex items-center gap-2 mt-4 overflow-x-auto pb-2">
            <Link
              to="/"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                isActive('/') 
                  ? 'bg-red-50 text-red-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Home className="w-4 h-4" />
              Inicio
            </Link>

            <Link
              to="/planificacion"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                isActive('/planificacion') 
                  ? 'bg-red-50 text-red-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Planificación
            </Link>

            <Link
              to="/generar-documentos"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                isActive('/generar-documentos') 
                  ? 'bg-red-50 text-red-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-4 h-4" />
              Documentos
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
